import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import {
  agentDemoTestUrl,
  consoleAppOrigin,
  pickEnabledDemoToken,
  type DemoLinkSummary,
} from '@/lib/agentDemoTest';
import {
  enableWebPush,
  fetchPushServerStatus,
  getWebPushStatus,
  type WebPushStatus,
} from '@/lib/pushNotifications';
import type { PwaCalendarBooking } from '@/calendar/pwaCalendarUtils';
import { dateKeyFromDate } from '@/calendar/pwaCalendarUtils';
import {
  addLocalDays,
  formatStayWindowLabel,
  monthsOverlapping,
  rangeForPreset,
  rangeFromDateKeys,
  summarizeStayWindow,
  type StayWindowStats,
} from '@/calendar/homeStayStats';
import { HomeActivityChart, type HomeActivityDay } from '@/components/HomeActivityChart';
import { HomeLeadsPie, type HomeLeadSlice } from '@/components/HomeLeadsPie';

type Agent = { id: string; name: string; isActive: boolean };

type DashboardStats = {
  whatsappMetaCostEstimate?: {
    rateUsdPerMessage: number;
    market: string;
    note: string;
  };
  period?: {
    conversations: number;
    newConversations?: number;
    messages: number;
    startedWithoutFollowUp?: number;
    leads?: HomeLeadSlice[];
    whatsappOutboundMessages: number;
    whatsappEstimatedUsd: number;
  };
};

function Stat({ value, label, title }: { value: number | string; label: string; title?: string }) {
  const shown =
    typeof value === 'string' ? value : Number.isFinite(value) ? value.toLocaleString('es') : '—';
  return (
    <div className="home-stat" title={title}>
      <div className="home-stat__value">{shown}</div>
      <div className="home-stat__label">{label}</div>
    </div>
  );
}

function formatUsdEstimate(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

type StayPreset = 'today' | 'week' | 'fortnight' | 'custom';

const STAY_PRESETS: Array<{ id: StayPreset; label: string }> = [
  { id: 'today', label: 'Hoy' },
  { id: 'week', label: 'Semana' },
  { id: 'fortnight', label: '14 días' },
  { id: 'custom', label: 'Rango' },
];

function pushStatusHint(status: WebPushStatus): string | null {
  if (status === 'denied') {
    return 'Los permisos están bloqueados. Activá las notificaciones desde la configuración del navegador o del sistema.';
  }
  if (status === 'unsupported') {
    return 'Este dispositivo o navegador no admite notificaciones push.';
  }
  return null;
}

export function HomePage() {
  const { get, post, can, permissionsLoaded } = useSession();
  const canPush = can('pwa.notifications.read');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [hasCalendar, setHasCalendar] = useState<boolean | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [pushStatus, setPushStatus] = useState<WebPushStatus | null>(null);
  const [pushConfigured, setPushConfigured] = useState<boolean | null>(null);
  const [pushMsg, setPushMsg] = useState<string | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [demoTokens, setDemoTokens] = useState<Record<string, string | null>>({});
  const [testingId, setTestingId] = useState<string | null>(null);
  const [testErr, setTestErr] = useState<string | null>(null);
  const [testFallback, setTestFallback] = useState<{
    agentId: string;
    agentName: string;
    url: string;
  } | null>(null);
  const [stayPreset, setStayPreset] = useState<StayPreset>('today');
  const [chartDay, setChartDay] = useState<string | null>(null);
  const [customFrom, setCustomFrom] = useState(() => dateKeyFromDate(new Date()));
  const [customTo, setCustomTo] = useState(() => dateKeyFromDate(addLocalDays(new Date(), 13)));
  const [stayStats, setStayStats] = useState<StayWindowStats | null>(null);
  const [stayLabel, setStayLabel] = useState('');
  const [stayLoading, setStayLoading] = useState(false);
  const [stayErr, setStayErr] = useState<string | null>(null);
  const [activityDays, setActivityDays] = useState<HomeActivityDay[] | null>(null);
  const [activityErr, setActivityErr] = useState<string | null>(null);

  const refreshPushStatus = useCallback(async () => {
    if (!canPush) return;
    const server = await fetchPushServerStatus(get);
    if (server) setPushConfigured(server.configured);
    const status = await getWebPushStatus(server);
    setPushStatus(status);
  }, [canPush, get]);

  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const [rows, cal] = await Promise.all([
            get<Agent[]>('/api/console/agents'),
            get<{ hasCalendar: boolean }>('/api/console/calendario/has-calendar'),
          ]);
          if (!cancelled) {
            setAgents(rows);
            setHasCalendar(cal.hasCalendar);
          }
        } catch (e) {
          if (!cancelled) setErr(e instanceof ApiError ? e.message : 'Error al cargar');
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [get]);

  useEffect(() => {
    if (agents.length === 0) return;
    let cancelled = false;
    void (async () => {
      const entries = await Promise.all(
        agents.map(async (agent) => {
          try {
            const links = await get<DemoLinkSummary[]>(`/api/agents/${agent.id}/demo`);
            return [agent.id, pickEnabledDemoToken(links)] as const;
          } catch {
            return [agent.id, null] as const;
          }
        }),
      );
      if (!cancelled) {
        setDemoTokens(Object.fromEntries(entries));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agents, get]);

  const openAgentTest = useCallback(
    async (agent: Agent) => {
      const winName = `va-agent-test-${agent.id}`;
      setTestErr(null);
      setTestFallback(null);

      const openUrl = (url: string, popup: Window | null) => {
        if (popup && !popup.closed) {
          popup.location.replace(url);
          popup.focus();
          return;
        }
        const opened = window.open(url, winName);
        if (!opened) {
          setTestFallback({ agentId: agent.id, agentName: agent.name, url });
        }
      };

      const popup = window.open('about:blank', winName);
      setTestingId(agent.id);
      try {
        let token = demoTokens[agent.id] ?? null;
        if (!token) {
          const links = await get<DemoLinkSummary[]>(`/api/agents/${agent.id}/demo`);
          token = pickEnabledDemoToken(links);
        }
        if (!token) {
          const created = await post<DemoLinkSummary>(`/api/agents/${agent.id}/demo`, {
            config: {
              agentName: agent.name,
              primaryColor: '#7c3aed',
              accentColor: '#06b6d4',
              welcomeMessage: `¡Hola! Soy ${agent.name}. ¿En qué puedo ayudarte?`,
              showPoweredBy: true,
              theme: 'system',
            },
          });
          token = created.token;
        }
        if (!token) {
          throw new Error('No se pudo obtener el link de test');
        }
        setDemoTokens((prev) => ({ ...prev, [agent.id]: token }));
        const issued = await post<{ pass: string }>(`/api/agents/${agent.id}/demo/test-pass`, { token });
        const url = agentDemoTestUrl(
          consoleAppOrigin(window.location, import.meta.env.VITE_CONSOLE_ORIGIN),
          token,
          Date.now(),
          issued.pass,
        );
        openUrl(url, popup);
      } catch (e) {
        if (popup && !popup.closed) popup.close();
        if (e instanceof ApiError && e.status === 403) {
          setTestErr('No tenés permiso para abrir el test, o este agente todavía no tiene un link. Generá uno en el backoffice, en Test.');
        } else if (e instanceof ApiError) {
          setTestErr(e.message);
        } else {
          setTestErr('No se pudo abrir el test del agente.');
        }
      } finally {
        setTestingId(null);
      }
    },
    [demoTokens, get, post],
  );

  const presetRange = useMemo(() => {
    if (stayPreset === 'custom') return rangeFromDateKeys(customFrom, customTo);
    return rangeForPreset(stayPreset);
  }, [customFrom, customTo, stayPreset]);

  const selectedRange = useMemo(() => {
    if (!chartDay || !activityDays) return presetRange;
    const day = activityDays.find((item) => item.date === chartDay);
    if (!day) return presetRange;
    const start = new Date(day.from);
    const end = new Date(day.to);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) return presetRange;
    return { start, end };
  }, [activityDays, chartDay, presetRange]);

  useEffect(() => {
    if (!selectedRange) {
      setStayStats(null);
      setStayLabel('');
      setStayLoading(false);
      setStats(null);
      setStayErr('Elegí un rango válido de hasta 92 días.');
      return;
    }
    setStayErr(null);
    setStayLabel(formatStayWindowLabel(selectedRange));
    let cancelled = false;
    const params = new URLSearchParams({
      from: selectedRange.start.toISOString(),
      to: selectedRange.end.toISOString(),
    });
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const dash = await get<DashboardStats>(`/api/console/dashboard-stats?${params.toString()}`);
          if (!cancelled) setStats(dash);
        } catch (e) {
          if (!cancelled) {
            setStats(null);
            setErr(e instanceof ApiError ? e.message : 'Error al cargar');
          }
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [get, selectedRange]);

  useEffect(() => {
    let cancelled = false;
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const data = await get<{ days: HomeActivityDay[] }>('/api/console/home-activity');
          if (!cancelled) setActivityDays(data.days);
        } catch (e) {
          if (!cancelled) {
            setActivityDays(null);
            setActivityErr(e instanceof ApiError ? e.message : 'No se pudo cargar la actividad');
          }
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [get]);

  useEffect(() => {
    if (hasCalendar !== true || !selectedRange) return;
    let cancelled = false;
    setStayLoading(true);
    const range = selectedRange;
    const t = window.setTimeout(() => {
      void (async () => {
        try {
          const months = monthsOverlapping(range);
          const [resourceRows, ...monthRows] = await Promise.all([
            get<{ resources: Array<{ id: string }> }>('/api/console/calendario/resources'),
            ...months.map((month) =>
              get<{ bookings: PwaCalendarBooking[] }>(
                `/api/console/calendario/bookings?year=${month.year}&month=${month.month}`,
              ),
            ),
          ]);
          if (cancelled) return;
          const bookings = monthRows.flatMap((row) => row.bookings ?? []);
          setStayStats(summarizeStayWindow(bookings, resourceRows.resources?.length ?? 0, range));
        } catch (e) {
          if (!cancelled) {
            setStayStats(null);
            setStayErr(e instanceof ApiError ? e.message : 'No se pudieron cargar las estadías');
          }
        } finally {
          if (!cancelled) setStayLoading(false);
        }
      })();
    }, 0);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [get, hasCalendar, selectedRange]);

  useEffect(() => {
    if (!canPush || !permissionsLoaded) return;
    let cancelled = false;
    void (async () => {
      const server = await fetchPushServerStatus(get);
      if (cancelled) return;
      if (server) setPushConfigured(server.configured);
      const status = await getWebPushStatus(server);
      if (!cancelled) setPushStatus(status);
    })();
    return () => {
      cancelled = true;
    };
  }, [canPush, permissionsLoaded, get]);

  useEffect(() => {
    if (!canPush || !permissionsLoaded) return;
    const onResume = () => void refreshPushStatus();
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    return () => {
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
    };
  }, [canPush, permissionsLoaded, refreshPushStatus]);

  async function enablePush() {
    setPushMsg(null);
    setPushBusy(true);
    try {
      const result = await enableWebPush(get, post);
      if (result.ok) {
        setPushStatus('active');
        setPushMsg(null);
      } else {
        setPushMsg(result.message);
        await refreshPushStatus();
      }
    } finally {
      setPushBusy(false);
    }
  }

  const pushHint = pushStatus ? pushStatusHint(pushStatus) : null;
  const showPushCta =
    canPush && permissionsLoaded && pushStatus !== null && pushStatus !== 'active';

  return (
    <div>
      <h1 className="pwa-page-title">Inicio</h1>
      {err ? <p className="pwa-err">{err}</p> : null}

      {showPushCta ?
        <section className="home-push-cta pwa-card" aria-label="Activar notificaciones push">
          <div className="home-push-cta__icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="currentColor" width="28" height="28">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.64 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2zm-2 1H8v-6c0-2.48 1.51-4.5 4-4.5s4 2.02 4 4.5v6z" />
            </svg>
          </div>
          <div className="home-push-cta__body">
            <h2 className="home-push-cta__title">Activá las notificaciones push</h2>
            <p className="home-push-cta__text">
              Recibí avisos de nuevos mensajes y alertas aunque la app esté en segundo plano.
            </p>
            {pushConfigured === false ?
              <p className="home-push-cta__hint">
                El servidor aún no tiene Web Push (VAPID) configurado. Pedile al administrador que agregue las claves
                en el API antes de activar acá.
              </p>
            : null}
            {pushHint ? <p className="home-push-cta__hint">{pushHint}</p> : null}
            {pushMsg ? <p className="home-push-cta__msg">{pushMsg}</p> : null}
            {pushStatus !== 'denied' && pushStatus !== 'unsupported' && pushConfigured !== false ?
              <button
                type="button"
                className="pwa-btn pwa-btn--primary home-push-cta__btn"
                disabled={pushBusy}
                onClick={() => void enablePush()}
              >
                {pushBusy ? 'Activando…' : 'Activar notificaciones'}
              </button>
            : null}
          </div>
        </section>
      : null}

      <div className="home-period" role="group" aria-label="Período">
        {STAY_PRESETS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            className={chartDay == null && stayPreset === preset.id ? 'home-period__btn home-period__btn--on' : 'home-period__btn'}
            aria-pressed={chartDay == null && stayPreset === preset.id}
            onClick={() => {
              setChartDay(null);
              setStayPreset(preset.id);
            }}
          >
            {preset.label}
          </button>
        ))}
      </div>
      {chartDay == null && stayPreset === 'custom' ?
        <div className="home-period__range">
          <label>
            Desde
            <input
              className="pwa-field home-period__date"
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(event) => setCustomFrom(event.target.value)}
            />
          </label>
          <label>
            Hasta
            <input
              className="pwa-field home-period__date"
              type="date"
              value={customTo}
              min={customFrom}
              onChange={(event) => setCustomTo(event.target.value)}
            />
          </label>
        </div>
      : null}
      {stayLabel ? <p className="home-period__caption">{stayLabel}</p> : null}
      {stayErr ? <p className="pwa-err">{stayErr}</p> : null}

      {activityErr ? <p className="pwa-err">{activityErr}</p> : null}
      {activityDays ?
        <HomeActivityChart
          days={activityDays}
          selectedDate={chartDay}
          onSelectDate={(date) => setChartDay((current) => (current === date ? null : date))}
        />
      : !activityErr ?
        <p className="pwa-muted">Cargando actividad…</p>
      : null}

      {hasCalendar ?
        <section aria-label="Estadías del calendario" style={{ marginBottom: '1.25rem' }}>
          <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
            Estadías
          </h2>
          {stayStats ?
            <div className="home-stats">
              <Stat value={stayStats.occupiedCabins} label={`Ocupadas de ${stayStats.cabinCount}`} />
              <Stat
                value={stayStats.freeCabins}
                label={stayStats.days === 1 ? 'Libres hoy' : 'Libres en todo el período'}
              />
              <Stat value={stayStats.arrivals} label="Llegadas" />
              <Stat value={stayStats.departures} label="Salidas" />
            </div>
          : stayLoading ?
            <p className="pwa-muted">Cargando estadías…</p>
          : null}
        </section>
      : null}

      {stats?.period ?
        <section aria-label="Resumen de uso">
          <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
            Uso de la aplicación
          </h2>
          <div className="home-stats">
            <Stat
              value={stats.period.newConversations ?? 0}
              label="Conversaciones nuevas"
              title="Conversaciones creadas en el período."
            />
            <Stat value={stats.period.conversations} label="Conversaciones activas" />
            <Stat value={stats.period.messages} label="Mensajes" />
            <Stat
              value={stats.period.startedWithoutFollowUp ?? 0}
              label="Iniciadas sin respuesta"
              title="El usuario escribió primero, el agente respondió y no hubo otro mensaje del usuario."
            />
          </div>
          <p className="pwa-muted" style={{ fontSize: '0.75rem', margin: '-0.25rem 0 0', lineHeight: 1.35 }}>
            Iniciadas sin respuesta: el usuario escribió, el agente contestó y la conversación no siguió.
          </p>
        </section>
      : !err && !stayErr ?
        <p className="pwa-muted">Cargando estadísticas…</p>
      : null}

      {stats?.period ? <HomeLeadsPie slices={stats.period.leads ?? []} /> : null}

      {stats?.period && stats.whatsappMetaCostEstimate ?
        <section aria-label="Estimativo WhatsApp Meta" style={{ marginTop: '1.25rem' }}>
          <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.35rem' }}>
            Estimativo WhatsApp Meta
          </h2>
          <p className="pwa-muted" style={{ fontSize: '0.75rem', margin: '0 0 0.5rem', lineHeight: 1.35 }}>
            {stats.whatsappMetaCostEstimate.note} Tarifa{' '}
            {formatUsdEstimate(stats.whatsappMetaCostEstimate.rateUsdPerMessage)}/msg (
            {stats.whatsappMetaCostEstimate.market}).
          </p>
          <div className="home-stats">
            <Stat value={stats.period.whatsappOutboundMessages} label="Msgs salientes" />
            <Stat
              value={formatUsdEstimate(stats.period.whatsappEstimatedUsd)}
              label="Estimado USD"
            />
          </div>
        </section>
      : null}

      <p className="pwa-muted" style={{ marginTop: '0.5rem' }}>
        {hasCalendar === null ?
          '…'
        : hasCalendar ?
          <>
            Hay calendario activo. Abrí <Link to="/calendar">Calendario</Link> o los <Link to="/conversations">Chats</Link>{' '}
            desde la barra inferior.
          </>
        : 'Ningún agente tiene el plugin de calendario activo.'}
      </p>

      <section style={{ marginTop: '1.5rem' }}>
        <h2 className="pwa-muted" style={{ fontSize: '0.95rem', fontWeight: 600, margin: '0 0 0.5rem' }}>
          Agentes
        </h2>
        <p className="pwa-muted" style={{ marginTop: 0 }}>
          {can('pwa.calendar.read') ?
            'Tocá un agente para administrar las propiedades de sus recursos. '
          : null}
          Test abre el chat de prueba y empieza una conversación nueva cada vez. Reglas lista, edita y borra las reglas del agente.
        </p>
        {testErr ? <p className="pwa-err">{testErr}</p> : null}
        {testFallback ?
          <p className="pwa-muted">
            El navegador bloqueó la ventana.{' '}
            <a href={testFallback.url} target={`va-agent-test-${testFallback.agentId}`} rel="noopener noreferrer">
              Abrir test de {testFallback.agentName}
            </a>
          </p>
        : null}
        <ul className="home-agent-list">
          {agents.map((a) => (
            <li key={a.id} className="home-agent">
              <div className="pwa-card home-agent__card">
                <div className="home-agent__row">
                  {can('pwa.calendar.read') ?
                    <Link
                      to={`/agents/${a.id}/recursos`}
                      state={{ agentName: a.name }}
                      className="home-agent__main pwa-card--press"
                    >
                      <span style={{ fontWeight: 600 }}>{a.name}</span>
                      {!a.isActive ? <span className="pwa-muted" style={{ marginLeft: 8 }}>(inactivo)</span> : null}
                    </Link>
                  : <div className="home-agent__main">
                      <span style={{ fontWeight: 600 }}>{a.name}</span>
                      {!a.isActive ? <span className="pwa-muted" style={{ marginLeft: 8 }}>(inactivo)</span> : null}
                    </div>}
                  <div className="home-agent__actions">
                    <button
                      type="button"
                      className="home-agent__action"
                      aria-label={`Abrir test de ${a.name}`}
                      disabled={testingId === a.id}
                      onClick={() => void openAgentTest(a)}
                    >
                      {testingId === a.id ? '…' : 'Test'}
                    </button>
                    <Link
                      to={`/agents/${a.id}/reglas`}
                      state={{ agentName: a.name }}
                      className="home-agent__action"
                    >
                      Reglas
                    </Link>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
